import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { useMe } from "@/features/auth";
import {
  ChallengeBoard,
  ChallengeWindows,
  CheckInSheet,
  shortWindow,
  useToday,
  useWindows,
} from "@/features/checkins";
import { errorMessage } from "@/i18n/errors";
import { formatDate, formatDay, monthAndYear, todayIn } from "@/shared/lib/format";
import {
  Avatar,
  Banner,
  Button,
  Icon,
  List,
  ListRow,
  Screen,
  Sheet,
  Skeleton,
  StatusPill,
  Toggle,
  TruncatedText,
  useToast,
} from "@/shared/ui";

import {
  useChallenge,
  useLeave,
  useUnschedule,
  useVote,
  useWithdrawChallenge,
  type Challenge,
} from "../api";
import styles from "../challenges.module.css";
import {
  describeDayMin,
  describeLength,
  describeMeasure,
  describeProof,
  describeRule,
} from "../describe";
import { ChallengeIcon } from "../components/ChallengeIcon";
import { PhasePill } from "../components/PhasePill";
import { ParticipantsSheet } from "../components/ParticipantsSheet";
import { ScheduleSheet } from "../components/ScheduleSheet";

/** One challenge: what it asks, how the crew is doing, who takes part and who proposed it. */
export function ChallengeRoute() {
  const { t } = useTranslation();
  const { id = "" } = useParams();
  const challenge = useChallenge(id);

  if (challenge.isPending) {
    return (
      <Screen title={t("challenges.title")}>
        <Skeleton lines={5} />
      </Screen>
    );
  }
  if (challenge.error || !challenge.data) {
    return (
      <Screen title={t("challenges.title")}>
        <Banner tone="danger" title={errorMessage(t, challenge.error)} />
      </Screen>
    );
  }
  return <ChallengeScreen challenge={challenge.data} />;
}

function ChallengeScreen({ challenge }: { challenge: Challenge }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const me = useMe();
  const member = me.data?.member;
  const timeZone = me.data?.crew?.timezone ?? "UTC";
  const leave = useLeave(challenge.id);
  const withdraw = useWithdrawChallenge();
  const unschedule = useUnschedule(challenge.id);
  const vote = useVote();
  const [withdrawing, setWithdrawing] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [keepWatching, setKeepWatching] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const today = useToday({ enabled: challenge.phase === "active" && challenge.taking_part });

  const mine = challenge.mine;
  const isAdmin = member?.role === "admin";
  const proposal = challenge.state === "proposed";
  const notStarted = challenge.phase === "upcoming";
  const target = describeDayMin(t, challenge, i18n.language);

  const facts = [
    ...(proposal ? [describeLength(t, challenge)] : []), // once scheduled, the dates say it
    describeRule(t, challenge, i18n.language),
    describeMeasure(t, challenge),
    describeProof(t, challenge),
  ];
  const { period_start: first, start_date: start, end_date: end } = challenge;
  // One month by its name ("November 2026"), anything else as dates.
  const whole =
    first && end
      ? challenge.period_kind === "month" && challenge.period_length === 1
        ? monthAndYear(first, i18n.language)
        : t("challenges.range", {
            from: formatDay(first, i18n.language),
            to: formatDay(end, i18n.language),
          })
      : null;
  const period =
    whole && start
      ? start === first
        ? whole
        : t("challenges.periodFrom", { period: whole, date: formatDay(start, i18n.language) })
      : null;
  const footnotes = [
    period && t("challenges.periodLine", { period }),
    t("challenges.proposedBy", {
      name: challenge.created_by?.display_name ?? t("challenges.someone"),
      date: formatDate(challenge.created_at, i18n.language, timeZone),
    }),
    challenge.chosen_by &&
      challenge.chosen_at &&
      t("challenges.chosenBy", {
        name: challenge.chosen_by.display_name,
        date: formatDate(challenge.chosen_at, i18n.language, timeZone),
      }),
    proposal && t("challenges.votes", { n: challenge.vote_count }),
  ].filter((line): line is string => Boolean(line));
  const running = challenge.state === "chosen" && challenge.phase !== "upcoming";
  const myToday = today.data?.challenges.find((c) => c.id === challenge.id);
  const canCheckIn =
    challenge.phase === "active" &&
    myToday !== undefined &&
    (myToday.settled === false || myToday.state === "partial");

  const started = challenge.phase === "active";
  const windowed = challenge.window !== "day";
  // Leaving today cuts the window today is in: say what it then asks for.
  const todayDay = todayIn(timeZone);
  const ending = useWindows(
    challenge.id,
    { until: todayDay },
    { enabled: leaving && started && windowed },
  );
  const lastWindow = ending.data?.find((w) => w.last === todayDay);
  const leaveNote = lastWindow && shortWindow(t, lastWindow, challenge, i18n.language, "leave");
  const watching = challenge.watching;
  // Stop watching, leave a running challenge, or opt out: the same sheet with its own words.
  const way = watching ? "stopWatching" : started ? "leave" : "optOut";
  const leaveWord = t(`challenges.${way}`);
  const leaveTitle = t(`challenges.${way}Title`);
  const leaveBody = t(`challenges.${way}Body`);
  const leaveDone = t(
    `challenges.${{ stopWatching: "stoppedWatching", leave: "left", optOut: "optedOut" }[way]}`,
  );
  const watchers = challenge.participants.some((p) => p.role === "viewer") && (
    <>
      <h2 className={styles.sectionTitle}>{t("challenges.watchingTitle")}</h2>
      <ParticipantList challenge={challenge} meId={member?.id} role="viewer" />
    </>
  );
  const leaveNow = () =>
    leave.mutate(
      { keepWatching },
      {
        onSuccess: () => {
          toast(leaveDone, "success");
          if (keepWatching) setLeaving(false);
          else navigate("/challenges", { replace: true });
        },
      },
    );

  return (
    <Screen title={challenge.title}>
      <div className={styles.header}>
        <ChallengeIcon icon={challenge.icon} />
        <ul className={styles.factPills} aria-label={t("challenges.factsLabel")}>
          {challenge.phase !== "active" && (
            <li>
              <PhasePill challenge={challenge} />
            </li>
          )}
          {facts.map((fact) => (
            <li key={fact}>
              <StatusPill>{fact}</StatusPill>
            </li>
          ))}
        </ul>
      </div>
      {target && <p className={styles.muted}>{target}</p>}
      {challenge.rules && <p>{challenge.rules}</p>}

      {proposal && (
        <div className={styles.actions}>
          {challenge.taking_part && (
            <Button
              variant={challenge.my_vote ? "primary" : "secondary"}
              size="lg"
              fullWidth
              icon={challenge.my_vote ? <Icon name="check" size={20} /> : undefined}
              aria-pressed={challenge.my_vote}
              loading={vote.isPending}
              onClick={() =>
                vote.mutate(
                  { id: challenge.id, vote: !challenge.my_vote },
                  { onError: (error) => toast(errorMessage(t, error), "error") },
                )
              }
            >
              {challenge.my_vote ? t("challenges.voted") : t("challenges.vote")}
            </Button>
          )}
          {isAdmin && (
            <Button
              size="lg"
              fullWidth
              icon={<Icon name="flag" size={20} />}
              onClick={() => setScheduling(true)}
            >
              {t("challenges.schedule.open")}
            </Button>
          )}
          {mine && (
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              icon={<Icon name="pencil" size={20} />}
              onClick={() => navigate(`/challenges/${challenge.id}/edit`)}
            >
              {t("challenges.edit")}
            </Button>
          )}
          {(mine || isAdmin) && (
            <Button
              variant="danger"
              fullWidth
              icon={<Icon name="trash" size={20} />}
              onClick={() => setWithdrawing(true)}
            >
              {t("challenges.withdraw")}
            </Button>
          )}
        </div>
      )}

      {isAdmin && challenge.state === "chosen" && notStarted && (
        <div className={styles.actions}>
          <Button variant="secondary" size="lg" fullWidth onClick={() => setScheduling(true)}>
            {t("challenges.schedule.move")}
          </Button>
          <Button
            variant="secondary"
            size="lg"
            fullWidth
            loading={unschedule.isPending}
            onClick={() =>
              unschedule.mutate(undefined, {
                onSuccess: () => toast(t("challenges.schedule.backDone"), "success"),
                onError: (error) => toast(errorMessage(t, error), "error"),
              })
            }
          >
            {t("challenges.schedule.back")}
          </Button>
        </div>
      )}

      {proposal && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("challenges.who.title")}</h2>
          <ParticipantList
            challenge={challenge}
            meId={member?.id}
            label={t("challenges.who.title")}
          />
          {watchers}
          {!challenge.taking_part && (
            <p className={styles.meta}>
              {watching ? t("challenges.watching") : t("challenges.who.adminOnly")}
            </p>
          )}
          {mine && (
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              icon={<Icon name="users" size={20} />}
              onClick={() => setInviting(true)}
            >
              {t("challenges.who.edit")}
            </Button>
          )}
        </section>
      )}

      {running && challenge.start_date && challenge.end_date && (
        <ChallengeBoard
          challengeId={challenge.id}
          title={challenge.title}
          unit={challenge.unit}
          startDate={challenge.start_date}
          endDate={challenge.end_date}
          timeZone={timeZone}
          meId={member?.id}
          fixedDays={challenge.window === "day"}
          leftOn={Object.fromEntries(
            challenge.participants.flatMap((p) => (p.left_on ? [[p.member.id, p.left_on]] : [])),
          )}
          joinedOn={Object.fromEntries(
            challenge.participants.flatMap((p) =>
              p.joined_on ? [[p.member.id, p.joined_on]] : [],
            ),
          )}
        />
      )}

      {challenge.state === "chosen" && windowed && (
        <ChallengeWindows challengeId={challenge.id} rule={challenge} />
      )}

      {challenge.punishments.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("challenges.punishments.title")}</h2>
          <List label={t("challenges.punishments.title")}>
            {challenge.punishments.map((p) => (
              <ListRow
                key={p.position}
                leading={<span className={styles.punishmentNumber}>{p.position}</span>}
                title={<TruncatedText text={p.text} />}
                subtitle={t(
                  p.proof_required
                    ? "challenges.punishments.withProof"
                    : "challenges.punishments.withDone",
                )}
              />
            ))}
          </List>
        </section>
      )}

      {canCheckIn && (
        <Button
          size="lg"
          fullWidth
          icon={<Icon name="check" size={20} />}
          onClick={() => setCheckingIn(true)}
        >
          {t("challenges.checkIn")}
        </Button>
      )}

      <ul className={styles.footnotes}>
        {footnotes.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      {challenge.state === "chosen" && (
        <section className={styles.section}>
          {!running && (
            <>
              <h2 className={styles.sectionTitle}>{t("challenges.participantsTitle")}</h2>
              <ParticipantList challenge={challenge} meId={member?.id} />
            </>
          )}
          {watchers}
          {watching && <p className={styles.meta}>{t("challenges.watching")}</p>}
          {(mine || isAdmin) && challenge.phase !== "finished" && (
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              icon={<Icon name="users" size={20} />}
              onClick={() => setInviting(true)}
            >
              {t("challenges.who.add")}
            </Button>
          )}
          {((challenge.phase !== "finished" && challenge.taking_part) || watching) && (
            <Button variant="danger" size="lg" fullWidth onClick={() => setLeaving(true)}>
              {leaveWord}
            </Button>
          )}
        </section>
      )}

      {checkingIn && (
        <CheckInSheet challengeId={challenge.id} onClose={() => setCheckingIn(false)} />
      )}

      {inviting && <ParticipantsSheet challenge={challenge} onClose={() => setInviting(false)} />}

      <Sheet
        open={leaving}
        onClose={() => setLeaving(false)}
        title={leaveTitle}
        closeLabel={t("common.close")}
      >
        <p className={styles.muted}>{leaveBody}</p>
        {leaveNote && <p className={styles.muted}>{leaveNote}</p>}
        {!watching && (
          <Toggle
            label={t("challenges.keepWatching")}
            description={t("challenges.keepWatchingNote")}
            checked={keepWatching}
            onChange={setKeepWatching}
          />
        )}
        {leave.error && <Banner tone="danger" title={errorMessage(t, leave.error)} />}
        <div className={styles.actions}>
          <Button variant="danger" size="lg" fullWidth loading={leave.isPending} onClick={leaveNow}>
            {leaveWord}
          </Button>
          <Button variant="secondary" size="lg" fullWidth onClick={() => setLeaving(false)}>
            {t("common.cancel")}
          </Button>
        </div>
      </Sheet>

      {scheduling && (
        <ScheduleSheet
          challenge={challenge}
          timeZone={timeZone}
          open
          onClose={() => setScheduling(false)}
        />
      )}

      <Sheet
        open={withdrawing}
        onClose={() => setWithdrawing(false)}
        title={t("challenges.withdrawTitle")}
        closeLabel={t("common.close")}
      >
        <p className={styles.muted}>{t("challenges.withdrawBody")}</p>
        {withdraw.error && <Banner tone="danger" title={errorMessage(t, withdraw.error)} />}
        <div className={styles.actions}>
          <Button
            variant="danger"
            size="lg"
            fullWidth
            loading={withdraw.isPending}
            onClick={() =>
              withdraw.mutate(challenge.id, {
                onSuccess: () => {
                  toast(t("challenges.withdrawn"), "success");
                  navigate("/challenges", { replace: true });
                },
              })
            }
          >
            {t("challenges.withdrawConfirm")}
          </Button>
          <Button variant="secondary" size="lg" fullWidth onClick={() => setWithdrawing(false)}>
            {t("common.cancel")}
          </Button>
        </div>
      </Sheet>
    </Screen>
  );
}

/** The people of one role on a challenge: who takes part (before the start: the board shows them
 * after), or who only watches. */
function ParticipantList({
  challenge,
  meId,
  role = "player",
  label,
}: {
  challenge: Challenge;
  meId?: string;
  role?: "player" | "viewer";
  label?: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <List
      label={
        label ??
        (role === "viewer" ? t("challenges.watchingTitle") : t("challenges.participantsTitle"))
      }
    >
      {challenge.participants
        .filter((p) => p.role === role)
        .map((p) => (
          <ListRow
            key={p.member.id}
            leading={<Avatar name={p.member.display_name} seed={p.member.avatar_seed} />}
            title={p.member.display_name}
            subtitle={
              p.left_on
                ? t("challenges.leftOn", { date: formatDay(p.left_on, i18n.language) })
                : undefined
            }
            trailing={
              p.member.id === meId ? (
                <StatusPill tone="accent">{t("crew.you")}</StatusPill>
              ) : undefined
            }
          />
        ))}
    </List>
  );
}
